/**
 * The three things the page sends: an order, an enquiry and a callback
 * request. Handover §5.7, §5.9 and §9.1.
 *
 * Every one of them goes to an Edge Function with the **publishable** key on
 * the `apikey` header; `supabaseFetch` is what enforces that a secret key
 * never does.
 *
 * **The request carries no price and no total**, and no quantity the server
 * works out for itself. It carries what the customer chose — the event, the
 * dishes, the contact details — and the server re-runs the engine against
 * `public.submission_context()` and decides (§19.2 principle 2).
 *
 * Run 15 items 1(j) and 2(a) add the one kind of quantity that **is** a
 * choice: the figure the customer set themselves, per dish and per drink.
 * The server still re-runs the engine; what it does with these is make the
 * named dishes exactly that many and store those lines as the customer's.
 *
 * The one figure the page sends that nothing reads is `client_gross`, which
 * `submit_order` records as ignored and never branches on: it is there so a
 * disagreement between the page and the server shows up in the data rather
 * than in a customer's complaint.
 */

import { supabaseFetch } from './api.ts'
import type { PublicConfig } from './env.ts'

export interface AddressPayload {
  postcode?: string
  town?: string
  street_and_number?: string
  floor_and_door?: string
}

export interface OrderPayload {
  event: {
    name?: string
    date?: string
    start?: string
    occasion?: string
    serving_window?: string
    guest_profile?: string
    balance?: string
    guests?: number
    gf_guests?: number
    dietary_note?: string
    /** `build/FIXES.md` item 73 (F2): the three answers the conference is made of. */
    conference?: { welcome: boolean; breaks: number; lunch: boolean }
    /** `build/FIXES.md` item 78 (F7): *Kér kávét is?* */
    coffee?: boolean
  }
  fulfilment: { kind?: string; slot_id?: string; postcode?: string }
  basket: {
    savoury?: string[]
    sweet?: string[]
    /**
     * Run 15 item 1(j): the quantities the customer set with − / +, in
     * portions, per dish. The server raises each to whole production units
     * and re-runs the engine; every other line is still the engine's own.
     */
    quantities?: { dish_id: string; portions: number }[]
    fruit?: string[]
    /** Run 15 item 2(a): a drink is a whole quantity, in bottles. */
    drinks?: { dish_id: string; portions: number }[]
  }
  customer: {
    is_business?: boolean
    last_name?: string
    first_name?: string
    email?: string
    phone?: string
    company?: string
    vat_number?: string
  }
  billing_address?: AddressPayload
  delivery_same_as_billing?: boolean
  delivery_address?: AddressPayload | null
  acceptance?: { terms?: boolean; cancellation?: boolean }
  enquiry_ticks?: { staff_or_equipment?: boolean; custom_menu?: boolean; planteen_only?: boolean }
  /** `build/FIXES.md` item 79 (F8): *Megjegyzés vagy különleges kérés*. */
  customer_comment?: string
  payment_method?: string
  lang?: string
  /** Display only. Recorded as ignored; nothing is decided from it. */
  client_gross?: number
}

export interface SubmitAnswer {
  ok: boolean
  /** `catalogue_changed`, 25 October 2026: the menu moved during submission. */
  outcome?: 'payment' | 'confirmation' | 'enquiry' | 'blocked' | 'callback' | 'catalogue_changed'
  message?: string
  review?: boolean
  /**
   * `build/FIXES.md` item 79 (F8): the server routed this order to
   * `awaiting_confirmation`. Nothing is paid, so the page starts no payment
   * and the confirmation panel says a colleague will answer.
   */
  confirmation?: boolean
  confirmation_reasons?: string[]
  order?: {
    ref: string
    public_token: string
    status: string
    lane: string
    payment_deadline: string | null
    freeze_at: string | null
    gross: number
  }
  payment?: { method: string; amount_gross: number; next: string }
  enquiry_id?: string
  callback_id?: string
  expires_at?: string
  code?: string
}

async function call(
  config: PublicConfig,
  name: string,
  body: unknown,
  fetchImpl: typeof fetch,
): Promise<SubmitAnswer> {
  const response = await supabaseFetch(
    config.supabaseUrl,
    `/functions/v1/${name}`,
    {
      method: 'POST',
      apiKey: config.supabasePublishableKey,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
    fetchImpl,
  )
  const answer = (await response.json().catch(() => ({}))) as SubmitAnswer
  if (answer.message === undefined && !response.ok) {
    /* No sentence came back at all. This module writes none: a customer
       sentence belongs to the page, which knows which language is on screen
       (§5.12). `code` says what happened, and the page's own
       `SUBMIT_UNAVAILABLE` is what it shows. */
    return { ok: false, outcome: 'blocked', code: 'transport' }
  }
  return answer
}

export const submitOrder = (
  config: PublicConfig,
  payload: OrderPayload,
  fetchImpl: typeof fetch = fetch,
): Promise<SubmitAnswer> => call(config, 'submit_order', payload, fetchImpl)

export interface EnquiryPayload {
  phone: string
  email?: string | null
  name?: string | null
  note?: string | null
  routed_by?: string
  ticks?: Record<string, boolean>
  event_params?: Record<string, unknown>
  basket?: unknown[]
}

export const submitEnquiry = (
  config: PublicConfig,
  payload: EnquiryPayload,
  fetchImpl: typeof fetch = fetch,
): Promise<SubmitAnswer> => call(config, 'submit_enquiry', payload, fetchImpl)

export interface CallbackPayload {
  phone: string
  email?: string | null
  name?: string | null
  note?: string | null
  event_date?: string | null
  event_params?: Record<string, unknown>
  basket?: unknown[]
  /** Run 12 item B: the page's language, for U17 and U18. */
  lang?: string
}

export const submitCallback = (
  config: PublicConfig,
  payload: CallbackPayload,
  fetchImpl: typeof fetch = fetch,
): Promise<SubmitAnswer> => call(config, 'submit_callback', payload, fetchImpl)

/** §9.4: the order as the return page reads it, by its public token. */
/**
 * SimplePay test prompt of 3 November 2026, §3: SimplePay's back URL, as the
 * server read it. `verified` is false unless `s` matched `r` under the
 * merchant's key and the transaction is this order's; the page then shows a
 * neutral error and no payment result. `transaction_id` is there on SUCCESS
 * and FAIL only.
 */
export interface BackAnswer {
  verified: boolean
  event?: 'SUCCESS' | 'FAIL' | 'CANCEL' | 'TIMEOUT'
  transaction_id?: string | null
}

export async function readOrderPublic(
  config: PublicConfig,
  token: string,
  fetchImpl: typeof fetch = fetch,
  back?: { r: string; s: string },
): Promise<{ ok: boolean; order?: Record<string, unknown>; message?: string; back?: BackAnswer }> {
  const query =
    `token=${encodeURIComponent(token)}` +
    (back === undefined ? '' : `&r=${encodeURIComponent(back.r)}&s=${encodeURIComponent(back.s)}`)
  const response = await supabaseFetch(
    config.supabaseUrl,
    `/functions/v1/order_public?${query}`,
    { method: 'GET', apiKey: config.supabasePublishableKey },
    fetchImpl,
  )
  return (await response.json().catch(() => ({ ok: false }))) as {
    ok: boolean
    order?: Record<string, unknown>
    message?: string
    back?: BackAnswer
  }
}

/**
 * The customer declines a confirmed, unpaid offer from the link in U22
 * (`/fizetes/{token}?decline=1`). `order_public` takes `action = "decline"`
 * and `public.confirmed_offer_decline` refuses anything that is not an
 * unpaid confirmed offer. Fix list walk of 10 October 2026: the server half
 * existed and the page never called it.
 */
export async function declineOffer(
  config: PublicConfig,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ ok: boolean; order?: Record<string, unknown>; message?: string }> {
  const response = await supabaseFetch(
    config.supabaseUrl,
    '/functions/v1/order_public',
    {
      method: 'POST',
      apiKey: config.supabasePublishableKey,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, action: 'decline' }),
    },
    fetchImpl,
  )
  return (await response.json().catch(() => ({ ok: false }))) as {
    ok: boolean
    order?: Record<string, unknown>
    message?: string
  }
}
